// /badges/u/:username: another member's shelf. Pinned badges first, then the
// collection by family. No rank and no progress: those are private.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useGetUserBadgesQuery } from "../../../services/rtkApi/badgesApi";
import { useAppSelector } from "../../../store/hooks";
import type { PublicHeldBadge } from "../types";
import BadgeDetailSheet from "./BadgeDetailSheet";
import BadgeShelves from "./BadgeShelves";
import { BadgeTile } from "./BadgeTile";
import { compareRarest } from "./badgeFormat";
import "./badgeCollection.css";

const MemberBadgesPage: React.FC = () => {
  const { username = "" } = useParams<{ username: string }>();
  const auth = useAppSelector((s) => s.auth);
  const booting = auth.bootstrapStatus !== "ready";
  const ready = !booting && !!auth.token;
  const { data, error, isLoading, refetch } = useGetUserBadgesQuery(username, { skip: !ready || !username });
  const [openCode, setOpenCode] = useState<string | null>(null);

  useEffect(() => {
    document.title = `NΛSOS ${username}'s badges`;
  }, [username]);

  const badges = useMemo(() => data?.badges ?? [], [data]);
  const shownName = data?.username || username;
  const isMe = !!auth.username && auth.username.toLowerCase() === shownName.toLowerCase();
  const featured = useMemo(() => {
    const pinned = (data?.pinned ?? [])
      .map((code) => badges.find((b) => b.code === code))
      .filter((b): b is PublicHeldBadge => !!b);
    return pinned.length ? pinned : [...badges].sort(compareRarest).slice(0, 3);
  }, [data, badges]);
  const openBadge = badges.find((b) => b.code === openCode) ?? null;
  const notFound = (error as FetchBaseQueryError | undefined)?.status === 404;

  let body: React.ReactNode;
  if (booting) {
    body = <p className="bcol-page__status">Loading</p>;
  } else if (!auth.token) {
    body = (
      <div className="bcol-empty">
        <p>Member shelves are for members. Log in to see them.</p>
        <Link className="bcol-btn bcol-btn--pin" to={`/login?return=${encodeURIComponent(`/badges/u/${username}`)}`}>
          Log in
        </Link>
      </div>
    );
  } else if (isLoading) {
    body = <p className="bcol-page__status">Loading</p>;
  } else if (notFound) {
    body = (
      <div className="bcol-empty">
        <p>No member called {username}.</p>
      </div>
    );
  } else if (error || !data) {
    body = (
      <div className="bcol-empty">
        <p>Could not load these badges right now.</p>
        <button type="button" className="bcol-btn" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  } else {
    body = (
      <>
        <p className="bcol-page__stats">
          {badges.length} {badges.length === 1 ? "badge" : "badges"}
        </p>
        {isMe && (
          <p className="bcol-page__note">
            This is how other members see your shelf. <Link to="/badges">Open your collection</Link>
          </p>
        )}
        {featured.length > 0 && (
          <section className="bcol-pinned" aria-labelledby="bcol-member-featured">
            <h2 id="bcol-member-featured" className="bcol-pinned__title">
              {data.pinned?.length ? "Pinned" : "Rarest"}
            </h2>
            <div className="bcol-pinned__row">
              {featured.map((b) => (
                <BadgeTile key={b.code} badge={b} size="card" onOpen={setOpenCode} />
              ))}
            </div>
          </section>
        )}
        {badges.length === 0 ? (
          <div className="bcol-empty bcol-empty--inline">
            <p>No badges yet.</p>
          </div>
        ) : (
          <BadgeShelves badges={badges} onOpen={setOpenCode} />
        )}
      </>
    );
  }

  return (
    <div className="bcol-page">
      <div className="bcol-page__inner">
        <header className="bcol-page__head">
          <h1 className="bcol-page__title">
            <span className="bcol-page__udot">.</span>
            {shownName}&apos;s badges
          </h1>
        </header>
        {body}
      </div>
      <BadgeDetailSheet
        badge={openBadge}
        held
        mode="member"
        ownerName={shownName}
        open={!!openBadge}
        onOpenChange={(open) => {
          if (!open) setOpenCode(null);
        }}
      />
    </div>
  );
};

export default MemberBadgesPage;
